import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Plus, Pause, Play, Archive, Eye, Edit3, Loader2, Radio, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
    listEsteiraGroups,
    pauseEsteiraGroup,
    resumeEsteiraGroup,
    archiveEsteiraGroup,
} from '@/services/api';
import type { EsteiraGroup, EsteiraStatus } from '@/types/esteira';
import EsteiraGroupFormDialog from './components/EsteiraGroupFormDialog';
import SyncEsteiraCategoriesDialog from './components/SyncEsteiraCategoriesDialog';

const STATUS_COLORS: Record<EsteiraStatus, string> = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    paused: 'bg-amber-50 text-amber-700 border-amber-200',
    archived: 'bg-gray-50 text-gray-500 border-gray-200',
};

const STATUS_LABELS: Record<EsteiraStatus, string> = {
    active: 'Ativo',
    paused: 'Pausado',
    archived: 'Arquivado',
};

export default function Esteira() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Default `active`: a tela abre só com grupos disparando.
    // Pausados/arquivados aparecem clicando nos filtros correspondentes.
    const [statusFilter, setStatusFilter] = useState<EsteiraStatus | 'all'>('active');
    const [formOpen, setFormOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<EsteiraGroup | null>(null);
    const [syncCatGroup, setSyncCatGroup] = useState<EsteiraGroup | null>(null);

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['esteira-groups', statusFilter],
        queryFn: () => listEsteiraGroups({
            status: statusFilter === 'all' ? undefined : statusFilter,
            per_page: 50,
        }),
        staleTime: 1000 * 30,
    });

    const groups = data?.items ?? [];

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['esteira-groups'] });
    };

    const pauseMutation = useMutation({
        mutationFn: pauseEsteiraGroup,
        onSuccess: () => { invalidate(); toast({ title: 'Grupo pausado' }); },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao pausar',
            description: e?.response?.data?.detail || 'Tente novamente',
        }),
    });

    const resumeMutation = useMutation({
        mutationFn: resumeEsteiraGroup,
        onSuccess: () => { invalidate(); toast({ title: 'Grupo reativado' }); },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao reativar',
            description: e?.response?.data?.detail || 'Tente novamente',
        }),
    });

    const archiveMutation = useMutation({
        mutationFn: archiveEsteiraGroup,
        onSuccess: () => { invalidate(); toast({ title: 'Grupo arquivado' }); },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao arquivar',
            description: e?.response?.data?.detail || 'Tente novamente',
        }),
    });

    const handleCreate = () => {
        setEditingGroup(null);
        setFormOpen(true);
    };

    const handleEdit = (g: EsteiraGroup) => {
        setEditingGroup(g);
        setFormOpen(true);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <Radio className="w-7 h-7 text-violet-600" />
                        Esteira de Broadcasts
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Fila infinita de envios consumindo o ms-template-intelligence —
                        templates rankeados por país e domínio.
                    </p>
                </div>
                <Button
                    onClick={handleCreate}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Novo grupo
                </Button>
            </div>

            {/* Filtros de status */}
            <div className="flex items-center gap-2">
                {(['all', 'active', 'paused', 'archived'] as const).map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            statusFilter === s
                                ? 'bg-violet-600 text-white border-violet-600'
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        {s === 'all' ? 'Todos' : STATUS_LABELS[s]}
                    </button>
                ))}
            </div>

            {/* Lista */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
                </div>
            ) : groups.length === 0 ? (
                <div className="rounded-2xl bg-white p-12 text-center border border-gray-100">
                    <div className="mx-auto w-16 h-16 bg-violet-50 rounded-full flex items-center justify-center mb-4">
                        <Radio className="w-8 h-8 text-violet-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        Nenhum grupo de esteira
                    </h3>
                    <p className="text-gray-500 mb-6 text-sm">
                        Crie um grupo pra começar a alimentar a fila com templates dinâmicos.
                    </p>
                    <Button
                        onClick={handleCreate}
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Criar grupo
                    </Button>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full divide-y divide-gray-100">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grupo</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">País</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domínios</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Páginas</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horários</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {groups.map((g) => (
                                <tr key={g.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-semibold text-gray-900">{g.name}</div>
                                        {g.description && (
                                            <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                                {g.description}
                                            </div>
                                        )}
                                        <div className="text-[10px] text-gray-400 mt-1">
                                            ID #{g.id}
                                            {g.template_intel_campaign_id && ` · TI #${g.template_intel_campaign_id}`}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge variant="outline" className="font-mono">
                                            {g.target_country}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4 max-w-xs">
                                        <div className="text-xs text-gray-700 line-clamp-2">
                                            {g.source_domains.join(', ')}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge variant="outline">{g.page_ids.length}</Badge>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-1">
                                            {g.schedule_times.map((t) => (
                                                <Badge key={t} variant="outline" className="font-mono text-[10px]">
                                                    {t}
                                                </Badge>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge
                                            variant="outline"
                                            className={STATUS_COLORS[g.status]}
                                        >
                                            {STATUS_LABELS[g.status]}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-4 text-right whitespace-nowrap">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button
                                                variant="ghost" size="sm"
                                                onClick={() => navigate(`/meta/esteira/${g.id}`)}
                                                className="h-8 px-2 text-blue-600 hover:bg-blue-50"
                                                title="Detalhes"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            {(() => {
                                                // Detecta categorias únicas das pages vinculadas
                                                const cats = new Map<number, string>();
                                                (g.pages_meta || []).forEach(m => {
                                                    if (m.internal_category_id && m.internal_category_name) {
                                                        cats.set(m.internal_category_id, m.internal_category_name);
                                                    }
                                                });
                                                if (g.status === 'archived' || cats.size === 0) return null;
                                                return (
                                                    <Button
                                                        variant="ghost" size="sm"
                                                        onClick={() => setSyncCatGroup(g)}
                                                        className="h-8 px-2 text-indigo-600 hover:bg-indigo-50"
                                                        title={`Sincronizar ${cats.size} categoria${cats.size > 1 ? 's' : ''} (puxa pages novas)`}
                                                    >
                                                        <RefreshCw className="w-4 h-4" />
                                                    </Button>
                                                );
                                            })()}
                                            {g.status !== 'archived' && (
                                                <Button
                                                    variant="ghost" size="sm"
                                                    onClick={() => handleEdit(g)}
                                                    className="h-8 px-2 text-gray-600 hover:bg-gray-100"
                                                    title="Editar"
                                                >
                                                    <Edit3 className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {g.status === 'active' && (
                                                <Button
                                                    variant="ghost" size="sm"
                                                    onClick={() => pauseMutation.mutate(g.id)}
                                                    disabled={pauseMutation.isPending}
                                                    className="h-8 px-2 text-amber-600 hover:bg-amber-50"
                                                    title="Pausar"
                                                >
                                                    <Pause className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {g.status === 'paused' && (
                                                <Button
                                                    variant="ghost" size="sm"
                                                    onClick={() => resumeMutation.mutate(g.id)}
                                                    disabled={resumeMutation.isPending}
                                                    className="h-8 px-2 text-emerald-600 hover:bg-emerald-50"
                                                    title="Reativar"
                                                >
                                                    <Play className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {g.status !== 'archived' && (
                                                <Button
                                                    variant="ghost" size="sm"
                                                    onClick={() => {
                                                        if (confirm(`Arquivar "${g.name}"? Esta ação é irreversível.`)) {
                                                            archiveMutation.mutate(g.id);
                                                        }
                                                    }}
                                                    disabled={archiveMutation.isPending}
                                                    className="h-8 px-2 text-red-600 hover:bg-red-50"
                                                    title="Arquivar"
                                                >
                                                    <Archive className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <EsteiraGroupFormDialog
                open={formOpen}
                onOpenChange={(o) => {
                    setFormOpen(o);
                    if (!o) setEditingGroup(null);
                }}
                existingGroup={editingGroup}
                onSuccess={() => {
                    invalidate();
                    refetch();
                }}
            />

            <SyncEsteiraCategoriesDialog
                open={!!syncCatGroup}
                onOpenChange={(o) => { if (!o) setSyncCatGroup(null); }}
                groupId={syncCatGroup?.id || 0}
                groupName={syncCatGroup?.name || ''}
                categories={(() => {
                    if (!syncCatGroup) return [];
                    const map = new Map<number, string>();
                    (syncCatGroup.pages_meta || []).forEach(m => {
                        if (m.internal_category_id && m.internal_category_name) {
                            map.set(m.internal_category_id, m.internal_category_name);
                        }
                    });
                    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
                })()}
            />
        </div>
    );
}
